/** Game / Cognitive Studio API types (spec v2.1) */

export const GAME_IDS = {
  sequenceRecall: "sequence-recall",
  nBack: "n-back",
  wordFluencyTap: "word-fluency-tap",
  mathBlitz: "math-blitz",
  visualSearch: "visual-search",
  ruleSwitch: "rule-switch",
  patternMatch: "pattern-match",
  orderTheSteps: "order-the-steps",
  reactionTap: "reaction-tap",
  sizeSort: "size-sort",
  soundMatch: "sound-match",
  targetFocus: "target-focus",
  pathMemory: "path-memory",
  categoryTap: "category-tap",
  objectRotationMatch: "object-rotation-match",
  rhythmTap: "rhythm-tap",
} as const;

export type GameId = (typeof GAME_IDS)[keyof typeof GAME_IDS];

export type SessionType = "guided" | "freeplay";

/** POST /games/sessions — request body */
export interface GameSessionCreateRequest {
  sessionType: SessionType;
  gameId?: string; // required when sessionType === "freeplay"
  patientId: string;
}

/** Per-game entry in session create response */
export interface GameSessionGame {
  gameId: string;
  difficulty: number;
  contentSeed: string;
}

/** POST /games/sessions — response */
export interface GameSessionCreateResponse {
  sessionId: string;
  sessionType: SessionType;
  games: GameSessionGame[];
}

/** POST /games/sessions/:sessionId/rounds — request body */
export interface GameRoundSubmitRequest {
  gameId: string;
  roundNumber: number;
  difficulty: number;
  accuracy: number; // 0.0–1.0
  reactionTimeMs?: number | null; // mean ms, null for untimed games
  completionTimeMs?: number | null;
  errorCount?: number;
  rawData?: Record<string, unknown>;
}

/** POST /games/sessions/:sessionId/rounds — response */
export interface GameRoundSubmitResponse {
  xpEarned: number;
  domainUpdates: Record<string, number>;
  newDifficultyLevel: number | null;
}

export interface GameAchievementUnlocked {
  code: string;
  name: string;
  description: string;
  xpReward: number;
}

/** PATCH /games/sessions/:sessionId — response */
export interface GameSessionCompleteResponse {
  totalXp: number;
  newLevel: number;
  achievementsUnlocked: GameAchievementUnlocked[];
  streakDay: number;
  sessionType: SessionType;
}

/** GET /games/progress — response */
export interface GameProgressResponse {
  domains: Record<string, number>;
  trend: Array<{ date: string } & Record<string, number>>;
  level: number;
  xp: number;
  streakDay: number;
}

/** GET /games/summary — response (caregiver fields when ?patientId and caller is caregiver) */
export interface GameSummaryResponse {
  patientId?: string;
  lastActiveAt?: string;
  currentStreak?: number;
  domains: Record<string, number>;
  trend: Array<{ date: string } & Record<string, number>>;
  sessionFrequency?: {
    totalSessions: number;
    guidedSessions: number;
    freeplaySessions: number;
    last30Days: number;
  };
  domainDeltas?: Record<string, number>;
  failSafeTriggersLast30Days?: number;
  alerts?: Array<{ type: "inactivity" | "difficulty"; label: string }>;
}

/** GET /games — library item */
export interface GameLibraryItem {
  gameId: string;
  name: string;
  description: string;
  domains: string[];
  iconUrl: string;
  userLevel: number;
  lastPlayedAt: string | null;
  domainPriority: number; // 1 = most recommended
}

/** GET /games — response */
export interface GameLibraryResponse {
  games: GameLibraryItem[];
}

/** GET /games/:gameId/difficulty — response */
export interface GameDifficultyResponse {
  gameId: string;
  currentLevel: number;
  minLevel: number;
  maxLevel: number;
}
